/**
 * G6 — Deploy / version gate.
 *
 * After Railway (or any) publish, a long-lived admin tab can keep a baked JS
 * shell while `/runtime-config.json` (uncached; SW-ignored) already advertises
 * the new build. Poll that JSON and, on identity mismatch, run the same nuclear
 * clear+navigate ladder as Settings → Update (`forceAdminAppUpdateFromSettings`).
 *
 * Interaction with Apply-waiting SW banner (`updateReady` in AdminPwaLifecycle):
 * - Apply is the soft path when Workbox reports a **waiting** worker.
 * - This gate is independent: it does not wait for `waiting` and does not call
 *   `registration.update()` itself (Settings nuclear may probe SW as a side step).
 * - On mismatch it forces the nuclear ladder, which clears caches / unregisters
 *   SWs (including any waiting worker) and cache-bust navigates — superseding
 *   the Apply affordance when both would apply.
 * - Match → no force (Apply banner may still show for a normal SW update).
 *
 * Respects critical-flow + offline gates inside `forceAdminAppUpdateFromSettings`.
 * Polls only while the document is visible; schedules work via idle callback when available.
 */

import { getViteEnvString } from '../../shared/vite/readViteMetaEnv.js';
import { isAdminPwaViteDev } from './adminPwaViteDev.js';
import {
  forceAdminAppUpdateFromSettings,
  type ForceAdminAppUpdateOutcome,
} from './forceAdminAppUpdateFromSettings.js';

/** Bounded poll cadence while the tab stays visible (ms). */
export const ADMIN_DEPLOY_VERSION_POLL_INTERVAL_MS = 120_000;

/** Idle deadline before a scheduled check runs even if the browser stays busy. */
export const ADMIN_DEPLOY_VERSION_IDLE_TIMEOUT_MS = 2_000;

export const ADMIN_DEPLOY_VERSION_RUNTIME_CONFIG_PATH = '/runtime-config.json';

export type AdminDeployPublishedIdentity = {
  readonly buildId?: string;
  readonly buildVersion?: string;
};

export type AdminDeployVersionGateCheckOutcome =
  | 'match'
  | 'mismatch_forced'
  | 'skipped_no_baked'
  | 'skipped_no_published'
  | 'skipped_fetch_failed'
  | 'skipped_hidden'
  | 'skipped_vite_dev'
  | ForceAdminAppUpdateOutcome;

export function normalizeBuildIdentity(
  value: string | undefined | null,
): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Prefer `buildId`, then `buildVersion` (runtime-config / postbuild). */
export function resolvePublishedBuildIdentity(
  config: AdminDeployPublishedIdentity,
): string | null {
  return (
    normalizeBuildIdentity(config.buildId) ??
    normalizeBuildIdentity(config.buildVersion)
  );
}

/**
 * Baked shell identity: `VITE_BUILD_ID`, then `VITE_BUILD_VERSION`.
 * Injectable overrides keep unit tests free of Vite `import.meta`.
 */
export function resolveBakedBuildIdentity(options?: {
  readonly buildId?: string | undefined;
  readonly buildVersion?: string | undefined;
}): string | null {
  if (options !== undefined) {
    return (
      normalizeBuildIdentity(options.buildId) ??
      normalizeBuildIdentity(options.buildVersion)
    );
  }
  return (
    normalizeBuildIdentity(getViteEnvString('VITE_BUILD_ID')) ??
    normalizeBuildIdentity(getViteEnvString('VITE_BUILD_VERSION'))
  );
}

/** True only when both sides are known and differ — never force on missing data. */
export function isAdminDeployBuildMismatch(
  baked: string | null,
  published: string | null,
): boolean {
  if (baked === null || published === null) {
    return false;
  }
  return baked !== published;
}

export type AdminDeployVersionGateDeps = {
  readonly fetchPublished?: () => Promise<AdminDeployPublishedIdentity | null>;
  readonly resolveBaked?: () => string | null;
  readonly forceUpdate?: () => Promise<ForceAdminAppUpdateOutcome>;
  readonly isDocumentVisible?: () => boolean;
  readonly isViteDev?: () => boolean;
};

async function defaultFetchPublished(): Promise<AdminDeployPublishedIdentity | null> {
  try {
    const response = await fetch(ADMIN_DEPLOY_VERSION_RUNTIME_CONFIG_PATH, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as AdminDeployPublishedIdentity;
    return data;
  } catch {
    return null;
  }
}

function defaultIsDocumentVisible(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  return document.visibilityState === 'visible';
}

/**
 * One-shot compare: baked shell vs fresh `/runtime-config.json`.
 * On mismatch → nuclear force ladder. Match / incomplete data → no force.
 */
export async function checkAdminDeployVersionGate(
  deps: AdminDeployVersionGateDeps = {},
): Promise<AdminDeployVersionGateCheckOutcome> {
  const isViteDev = deps.isViteDev ?? isAdminPwaViteDev;
  if (isViteDev()) {
    return 'skipped_vite_dev';
  }

  const isDocumentVisible = deps.isDocumentVisible ?? defaultIsDocumentVisible;
  if (!isDocumentVisible()) {
    return 'skipped_hidden';
  }

  const baked = (deps.resolveBaked ?? resolveBakedBuildIdentity)();
  if (baked === null) {
    return 'skipped_no_baked';
  }

  const fetchPublished = deps.fetchPublished ?? defaultFetchPublished;
  const publishedConfig = await fetchPublished();
  if (publishedConfig === null) {
    return 'skipped_fetch_failed';
  }

  const published = resolvePublishedBuildIdentity(publishedConfig);
  if (published === null) {
    return 'skipped_no_published';
  }

  if (!isAdminDeployBuildMismatch(baked, published)) {
    return 'match';
  }

  const forceUpdate = deps.forceUpdate ?? forceAdminAppUpdateFromSettings;
  const outcome = await forceUpdate();
  if (outcome === 'started') {
    return 'mismatch_forced';
  }
  return outcome;
}

export type StartAdminDeployVersionGatePollOptions = AdminDeployVersionGateDeps & {
  readonly intervalMs?: number;
  readonly idleTimeoutMs?: number;
  readonly scheduleIdle?: (
    run: () => void,
    timeoutMs: number,
  ) => { cancel: () => void };
};

function defaultScheduleIdle(
  run: () => void,
  timeoutMs: number,
): { cancel: () => void } {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(() => run(), { timeout: timeoutMs });
    return {
      cancel: () => {
        window.cancelIdleCallback(id);
      },
    };
  }
  const timer = window.setTimeout(run, timeoutMs);
  return {
    cancel: () => {
      window.clearTimeout(timer);
    },
  };
}

/**
 * Bounded visible/idle poll. Returns a disposer (clear interval + listeners).
 * Stops further force attempts after a successful `mismatch_forced`.
 */
export function startAdminDeployVersionGatePoll(
  options: StartAdminDeployVersionGatePollOptions = {},
): () => void {
  const intervalMs = options.intervalMs ?? ADMIN_DEPLOY_VERSION_POLL_INTERVAL_MS;
  const idleTimeoutMs = options.idleTimeoutMs ?? ADMIN_DEPLOY_VERSION_IDLE_TIMEOUT_MS;
  const scheduleIdle = options.scheduleIdle ?? defaultScheduleIdle;
  const isViteDev = options.isViteDev ?? isAdminPwaViteDev;

  if (isViteDev()) {
    return () => undefined;
  }

  let disposed = false;
  let inFlight = false;
  let forced = false;
  let idleHandle: { cancel: () => void } | null = null;
  let intervalId: number | null = null;

  const runCheck = (): void => {
    if (disposed || forced || inFlight) {
      return;
    }
    inFlight = true;
    void checkAdminDeployVersionGate(options)
      .then((outcome) => {
        if (outcome === 'mismatch_forced') {
          forced = true;
        }
      })
      .finally(() => {
        inFlight = false;
      });
  };

  const scheduleCheck = (): void => {
    if (disposed || forced) {
      return;
    }
    idleHandle?.cancel();
    idleHandle = scheduleIdle(() => {
      idleHandle = null;
      runCheck();
    }, idleTimeoutMs);
  };

  const onVisibility = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      scheduleCheck();
    }
  };

  scheduleCheck();
  intervalId = window.setInterval(scheduleCheck, intervalMs);
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    disposed = true;
    idleHandle?.cancel();
    idleHandle = null;
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
